import DynamicSection from '../dynamic-section/dynamic-section';
import ProductReviews from '../product-reviews/product-reviews';
import { ProductManager } from '@/js/product-utils';

class ProductMain extends DynamicSection {
  connectedCallback() {
    this.initVariables();
    this.initManager();
    this.updateScrollPosition();
    this.updateTab();
    this.loadReviewsForProduct();
  }

  initVariables() {
    this.productUrl = this.querySelector('[data-product-url]').value;
    this.productId = this.querySelector('input[name="product-id"]').value;
  }

  /**
   * Initialize product manager for handling all product functionality
   * @returns {void}
   */
  initManager() {
    this.productManager = new ProductManager(this);
  }

  /**
   * Updates the active tab based on URL parameters
   *
   * Checks if there is a 'tab' parameter in the URL query string and updates
   * the product manager to show that tab if one exists. This allows deep linking
   * directly to specific tabs like one-time purchase or subscription options.
   *
   * @returns {void}
   */
  updateTab() {
    const urlSearchParameters = new URLSearchParams(globalThis.location.search);
    const tab = urlSearchParameters.get('tab');
    if (tab) {
      this.productManager.handleTabChange(tab);
    }
  }

  /**
   * Adjusts the scroll position when navigating to product reviews.
   *
   * When the URL contains the '#judgeme_product_reviews' hash, this method
   * scrolls the window up by the Judge.me settings offset value to properly
   * position the reviews section in view.
   *
   * @returns {void}
   */
  updateScrollPosition() {
    if (globalThis.location.hash === '#judgeme_product_reviews') {
      window.scrollBy(0, -globalThis.jdgmSettings.scrollTopOffset);
    }
  }

  /**
   * Handle updating the product form when a option value is selected.
   * @param {Event} event - The change event from the option input.
   */
  onOptionChange(event) {
    // From the checked options, created a comma separated list of the selected option value IDs
    const checkedInputs = this.querySelectorAll('[data-option] input:checked');
    const selectedOptionValues = [...checkedInputs]
      .map(input => input.value)
      .join(',');

    // Update the URL with the selected option value IDs
    const urlSearchParameters = new URLSearchParams(globalThis.location.search);
    urlSearchParameters.delete('variant');
    urlSearchParameters.set('option_values', selectedOptionValues);

    const productUrl =
      event.target.dataset.productUrl ?? globalThis.location.pathname;
    const updatedUrl = `${productUrl}?${urlSearchParameters.toString()}`;

    this.reRenderSection({
      url: updatedUrl,
      replaceState: false,
      replaceEntireSection: true,
      beforeReplaceMethod: this.doReplaceState,
      afterReplaceMethod: () => {
        this.loadReviewsForProduct();
      }
    });
  }

  loadReviewsForProduct() {
    ProductReviews.loadReviewsForProduct();
  }

  /**
   * Sets the URL with the new variant ID and product URL
   * @param {HTMLElement} element - The element containing the new variant information
   */
  doReplaceState(element) {
    const newVariantId = element.querySelector('[data-variant-id]').value;
    const newProductUrl = element.querySelector('[data-product-url]').value;

    const isNewVariant = newVariantId && this.variantId !== newVariantId;
    const isNewProduct = newProductUrl !== this.productUrl;

    if (isNewVariant) {
      // If its a valid variant, update the URL with the new variant ID and potentially new product URL
      const url = `${newProductUrl}?variant=${newVariantId}`;
      globalThis.history.replaceState(undefined, undefined, url);
    } else if (isNewProduct) {
      // even if its not a valid variant, update the URL with the new product URL if its different
      globalThis.history.replaceState(undefined, undefined, newProductUrl);
    }
  }
}

customElements.get('product-main') ||
  customElements.define('product-main', ProductMain);
